import { useEffect, useMemo } from "react";

import { flatMap, isEmpty, map, partition, sortBy, uniq } from "lodash/fp";

import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import useTopicReports from "@app/analytics/hooks/topics/useTopicReports";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import { ReportSource } from "@app/analytics/MeetingReports/types";
import type { TopicChannelProvider } from "@app/analytics/types";
import { useAuthenticated } from "@app/common/auth/useAuth";
import useMeetings from "@app/common/hooks/meetings/useMeetings";
import useTags from "@app/common/hooks/tags/useTags";
import type { DateRange } from "@app/common/types";
import * as SessionUtils from "@app/common/utils/sessionUtils";
import Config from "@app/config";

const useAllReports = ({ dateRange }: { dateRange: DateRange }) => {
  const { user } = useAuthenticated();
  const { teamId, userId } = useRollupContext();

  const myMeetingsQuery = useMeetings({
    dateRanges: [dateRange],
    userId,
    teamId,
    view: "default",
    includeSampleMeetings: true,
  });

  const sharedWithMeMeetingsQuery = useMeetings({
    dateRanges: [dateRange],
    userId,
    teamId,
    view: "shared_with_me",
  });

  const incompleteMeetingsQuery = useMeetings({
    dateRanges: [dateRange],
    workspaceId: user.workspaceId,
    userId,
    teamId,
    view: "incomplete",
  });

  const { topics, isLoading: isTopicsLoading } = useTopicReports({
    dateRange,
    view: "default",
  });

  const meetings = useMemo(
    () => [
      ...myMeetingsQuery.meetings,
      ...sharedWithMeMeetingsQuery.meetings,
      ...incompleteMeetingsQuery.meetings,
    ],
    [
      myMeetingsQuery.meetings,
      sharedWithMeMeetingsQuery.meetings,
      incompleteMeetingsQuery.meetings,
    ]
  );

  const isMeetingsLoading =
    myMeetingsQuery.isLoading ||
    sharedWithMeMeetingsQuery.isLoading ||
    incompleteMeetingsQuery.isLoading;

  const isLoading = Config.flags.waitForTopicReportsLoad
    ? isMeetingsLoading || isTopicsLoading
    : isMeetingsLoading;

  return {
    isLoading,
    meetings,
    topics,
  };
};

const usePopulateReportListMetadata = ({
  dateRange,
}: {
  dateRange: DateRange;
}) => {
  const { tags: userTags, isLoading: isUserTagsLoading } = useTags();

  const {
    isLoading: isReportsLoading,
    meetings,
    topics,
  } = useAllReports({ dateRange });

  const { initializeSources, initializeTags } = useReportsTable((state) => ({
    initializeSources: state.initializeSources,
    initializeTags: state.initializeTags,
  }));

  const meetingSources: ReportSource[] = useMemo(() => {
    return uniq(
      map(
        (m) =>
          SessionUtils.isUploadedSession(m) ? "upload" : m.meetingPlatform,
        meetings
      )
    );
  }, [meetings]);

  const topicProviders: TopicChannelProvider[] = useMemo(() => {
    return uniq(flatMap("channelProviders", topics));
  }, [topics]);

  useEffect(() => {
    if (isUserTagsLoading || isEmpty(userTags)) return;

    const [customTags, autoTags] = partition({ kind: "custom" }, userTags);

    initializeTags({
      all: userTags,
      byCategory: {
        auto: sortBy("name", autoTags),
        custom: sortBy("name", customTags),
      },
    });
  }, [isUserTagsLoading, userTags]);

  useEffect(() => {
    if (isReportsLoading) return;

    const allSources = uniq([...meetingSources, ...topicProviders]);
    initializeSources({ all: allSources });
  }, [isReportsLoading, meetingSources, topicProviders]);
};

export default usePopulateReportListMetadata;
